import { isAnalyticsConsentGranted } from './consent';

export type FirebasePublicConfig = {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId: string;
};

export type AnalyticsEnablementInput = {
  nodeEnvironment?: string;
  platformEnvironment?: string;
  explicitlyEnabled: boolean;
  hasCompleteConfig: boolean;
  consentGranted: boolean;
};

export const getFirebasePublicConfig = (): FirebasePublicConfig | null => {
  const apiKey = process.env.REACT_APP_FIREBASE_API_KEY?.trim();
  const authDomain = process.env.REACT_APP_FIREBASE_AUTH_DOMAIN?.trim();
  const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID?.trim();
  const storageBucket = process.env.REACT_APP_FIREBASE_STORAGE_BUCKET?.trim();
  const messagingSenderId = process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID?.trim();
  const appId = process.env.REACT_APP_FIREBASE_APP_ID?.trim();
  const measurementId = process.env.REACT_APP_FIREBASE_MEASUREMENT_ID?.trim();

  if (
    !apiKey ||
    !authDomain ||
    !projectId ||
    !storageBucket ||
    !messagingSenderId ||
    !appId ||
    !measurementId
  ) {
    return null;
  }

  return {
    apiKey,
    authDomain,
    projectId,
    storageBucket,
    messagingSenderId,
    appId,
    measurementId,
  };
};

export const isAnalyticsDebugEnabled = (): boolean =>
  process.env.REACT_APP_ANALYTICS_DEBUG === 'true';

export const isAnalyticsExplicitlyEnabled = (): boolean =>
  process.env.REACT_APP_ANALYTICS_ENABLED === 'true';

const getPlatformEnvironment = (): string =>
  (process.env.ENV_NAME || process.env.REACT_APP_ENV_NAME || '').trim().toUpperCase();

export const shouldEnableFirebaseAnalytics = ({
  nodeEnvironment,
  platformEnvironment,
  explicitlyEnabled,
  hasCompleteConfig,
  consentGranted,
}: AnalyticsEnablementInput): boolean => {
  if (nodeEnvironment === 'test' || !hasCompleteConfig || !consentGranted) {
    return false;
  }

  if (explicitlyEnabled) {
    return true;
  }

  return nodeEnvironment === 'production' && platformEnvironment?.toUpperCase() === 'PROD';
};

export const isFirebaseAnalyticsEnabled = (): boolean =>
  shouldEnableFirebaseAnalytics({
    nodeEnvironment: process.env.NODE_ENV,
    platformEnvironment: getPlatformEnvironment(),
    explicitlyEnabled: isAnalyticsExplicitlyEnabled(),
    hasCompleteConfig: getFirebasePublicConfig() !== null,
    consentGranted: isAnalyticsConsentGranted(),
  });

export const canOfferFirebaseAnalytics = (): boolean =>
  shouldEnableFirebaseAnalytics({
    nodeEnvironment: process.env.NODE_ENV,
    platformEnvironment: getPlatformEnvironment(),
    explicitlyEnabled: isAnalyticsExplicitlyEnabled(),
    hasCompleteConfig: getFirebasePublicConfig() !== null,
    consentGranted: true,
  });
